/**
 * useConnectionMutations — Supabase mutations for connection state changes.
 */
import { useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { ConnectionService } from '@/services/connections/connectionService';
import { PhoneConnectionResult } from '@/types/connections';
import { supabase } from '@/integrations/supabase/client';
import { initializeCSRFToken } from '@/utils/securityUtils';
import { logger } from '@/utils/logger';

export interface ConnectionMutations {
  sendConnectionRequest: (receiverId: string) => Promise<boolean>;
  acceptConnection: (connectionId: string) => Promise<boolean>;
  rejectConnection: (connectionId: string) => Promise<boolean>;
  removeConnection: (connectionId: string) => Promise<boolean>;
  sendPhoneConnectionRequest: (phoneData: { countryCode: string; phoneNumber: string }, preferredName?: string) => Promise<PhoneConnectionResult>;
}

/**
 * Hook for connection-related mutations
 * Provides methods to send, accept, reject, and remove connections
 */
export function useConnectionMutations(): ConnectionMutations {
  const { toast } = useToast();
  const { user } = useAuth();

  // Send a connection request to another user
  const sendConnectionRequest = useCallback(async (receiverId: string): Promise<boolean> => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "You must be signed in to send connection requests",
        variant: "destructive",
      });
      return false;
    }

    if (receiverId === user.id) {
      toast({
        title: "Invalid Connection",
        description: "You cannot send a connection request to yourself",
        variant: "destructive",
      });
      return false;
    }

    try {
      // Call the service with the correct parameters
      const result = await ConnectionService.sendConnectionRequest(user.id, receiverId);
      
      if (!result.success) {
        // Handle specific error cases with more user-friendly messages
        let errorMessage = result.error || "Failed to send connection request";
        let errorTitle = "Connection Request Failed";
        let variant: "default" | "destructive" = "destructive";
        
        // Handle specific error cases
        if (errorMessage.includes("recently rejected")) {
          errorTitle = "Connection Request Cooldown";
          // If we have cooldown information, include it in the message
          if (result.cooldownRemainingHours) {
            errorMessage = `This connection was recently rejected. Please wait ${result.cooldownRemainingHours} hour before trying again.`;
          }
          variant = "default";
        } else if (errorMessage.includes("already exists")) {
          errorTitle = "Connection Already Exists";
          errorMessage = "You already have a pending or accepted connection with this user";
          variant = "default";
        } else if (errorMessage.includes("with yourself")) {
          errorTitle = "Invalid Connection";
          errorMessage = "You cannot connect with yourself";
        }
        
        toast({
          title: errorTitle,
          description: errorMessage,
          variant: variant,
        });
        
        return false;
      }
      
      toast({
        title: "Connection Request Sent",
        description: "Your connection request has been sent",
      });
      
      return true;
    } catch (err: any) {
      logger.error("Unexpected error in sendConnectionRequest:", err);
      
      toast({
        title: "Connection Request Failed",
        description: err.message || "Could not send connection request",
        variant: "destructive",
      });
      
      return false;
    }
  }, [user, toast]);

  // Accept a connection request
  const acceptConnection = useCallback(async (connectionId: string): Promise<boolean> => {
    if (!user) return false;
    
    try {
      const result = await ConnectionService.updateConnectionStatus(connectionId, 'accepted');
      
      if (!result.success) {
        toast({
          title: "Failed to Accept Connection",
          description: result.error || "Could not accept the connection request",
          variant: "destructive",
        });
        return false;
      }
      
      toast({
        title: "Connection Accepted",
        description: "You are now connected with this user",
      });
      
      // Queue first-connection milestone (idempotent)
      try {
        await supabase.rpc("queue_pending_reward", {
          p_action: "first_connection" as any,
          p_points: 5,
          p_reference_id: connectionId,
          p_description: "You made your first connection",
        });
      } catch { /* fire-and-forget */ }
      
      return true;
    } catch (err: any) {
      logger.error("Unexpected error in acceptConnection:", err);
      
      toast({
        title: "Failed to Accept Connection",
        description: err.message || "Could not accept the connection request",
        variant: "destructive",
      });
      
      return false;
    }
  }, [user, toast]);

  // Reject a connection request
  const rejectConnection = useCallback(async (connectionId: string): Promise<boolean> => {
    if (!user) return false;
    
    try {
      const result = await ConnectionService.updateConnectionStatus(connectionId, 'rejected');
      
      if (!result.success) {
        toast({
          title: "Failed to Reject Connection",
          description: result.error || "Could not reject the connection request",
          variant: "destructive",
        });
        return false;
      }
      
      toast({
        title: "Connection Rejected",
        description: "The connection request has been rejected",
      });
      
      return true;
    } catch (err: any) {
      logger.error("Unexpected error in rejectConnection:", err);
      
      toast({
        title: "Failed to Reject Connection",
        description: err.message || "Could not reject the connection request",
        variant: "destructive",
      });
      
      return false;
    }
  }, [user, toast]);

  // Remove a connection
  const removeConnection = useCallback(async (connectionId: string): Promise<boolean> => {
    if (!user) return false;
    
    try {
      const result = await ConnectionService.removeConnection(connectionId);
      
      if (!result.success) {
        toast({
          title: "Failed to Remove Connection",
          description: result.error || "Could not remove the connection",
          variant: "destructive",
        });
        return false;
      }
      
      toast({
        title: "Connection Removed",
        description: "The connection has been removed",
      });
      
      return true;
    } catch (err: any) {
      logger.error("Unexpected error in removeConnection:", err);
      
      toast({
        title: "Failed to Remove Connection",
        description: err.message || "Could not remove the connection",
        variant: "destructive",
      });
      
      return false;
    }
  }, [user, toast]);

  // New method for phone-based connections with CSRF protection
  const sendPhoneConnectionRequest = useCallback(async (
    phoneData: { countryCode: string; phoneNumber: string }, 
    preferredName?: string
  ): Promise<PhoneConnectionResult> => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "You must be signed in to send connection requests",
        variant: "destructive",
      });
      return { success: false, error: "Authentication required" };
    }

    try {
      logger.debug("Sending phone connection request:", { phoneData, preferredName });
      
      // Initialize CSRF token
      const csrfToken = initializeCSRFToken();
      
      // Call edge function to create phone connection with CSRF protection
      const { data, error } = await supabase.functions.invoke('create_phone_connection', {
        body: {
          sender_id: user.id,
          phone_number: phoneData.phoneNumber,
          preferred_name: preferredName || null
        },
        headers: {
          'x-csrf-token': csrfToken
        }
      });
      
      if (error) {
        logger.error("Error sending phone connection request:", error);
        return { 
          success: false, 
          error: error.message || "Failed to send connection request" 
        };
      }
      
      if (data.error) {
        return { success: false, error: data.error };
      }
      
      logger.debug("Phone connection request successful:", data);
      
      return { 
        success: true, 
        data: data,
        connectionId: data.connection?.id
      };
    } catch (error: any) {
      logger.error("Error in sendPhoneConnectionRequest:", error);
      return { 
        success: false, 
        error: error.message || "An unexpected error occurred" 
      };
    }
  }, [user, toast]);

  return {
    sendConnectionRequest,
    acceptConnection,
    rejectConnection,
    removeConnection,
    sendPhoneConnectionRequest
  };
}
